import React, {
	useEffect,
	useMemo,
	useCallback,
	useState,
	useRef,
	memo,
	type ReactNode,
	type KeyboardEvent,
} from 'react';
import { styled } from '@compiled/react';
import BaseTable, { AutoResizer, type ColumnShape } from 'react-base-table';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { SpotlightTarget } from '@atlaskit/onboarding';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ConditionalWrapper } from '@atlassian/jira-business-conditional-wrapper';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	HEADER_X2C_NUDGE_STEP,
	useX2CNudgeStep,
} from '@atlassian/jira-project-create-importer-onboarding';
import ReadErrorIcon from '../../../common/assets/CannotReadFileError.svg';
import { DEFAULT_DATE_FORMAT } from '../../../common/constants/state';
import type { RowData, TableRefCurrent } from '../../../common/types';
import { hasTableColumnErrors } from '../../../common/utils';
import {
	useMappingState,
	useMigrationId,
	useAdvancedOptions,
	useProjectData,
} from '../../../controllers/import-state';
import { useSource } from '../../../controllers/import-state/selectors';
import { useGetPriorities } from '../../../services/get-priorities';
import { useReadCSVPreview } from '../../../services/read-csv';
import messages from './messages';
import { type ColumnWidths, getTableColumns, getNewScrollLeftPosition } from './utils';

const HEADER_HEIGHT = 64;

const MappingTable = () => {
	const { formatMessage } = useIntl();
	const [source] = useSource();
	const { isLoading, hasErrorOccurred, readCSVPreview } = useReadCSVPreview(source);
	const { data: priorities, isLoading: isPrioritiesLoading } = useGetPriorities();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const tableRef = useRef<TableRefCurrent>(null);
	const [projectData] = useProjectData();

	const [migrationId] = useMigrationId();
	const [mappingState, { setMappingState, setNextSelectedColumn }] = useMappingState();
	const [advancedOptions, { updateAdvancedOptions }] = useAdvancedOptions();
	const [columnWidths, setColumnWidths] = useState<ColumnWidths>({});
	const columns = useMemo(
		() => getTableColumns(mappingState, columnWidths, priorities),
		[columnWidths, mappingState, priorities],
	);
	const [{ nudgeStep }] = useX2CNudgeStep();

	const onReadCSV = useCallback(() => {
		if (migrationId !== null && mappingState === null) {
			readCSVPreview(migrationId, advancedOptions, projectData).then((resp) => {
				if (!resp) {
					setMappingState(null);
					return;
				}

				const { suggestedDateFormat, ...state } = resp;
				setMappingState(state);

				updateAdvancedOptions({
					dateFormat: suggestedDateFormat || DEFAULT_DATE_FORMAT,
				});

				fireTrackAnalytics(createAnalyticsEvent({}), 'csvFile read', {
					numberOfColumns: Object.keys(state.columns).length,
					numberOfMappedJiraFields: state.mappedJiraFieldIds.size,
					numberOfRows: state.rows.length,
					projectType: projectData.type,
					projectStyle: projectData.style,
					accessLevel: projectData.accessLevel,
				});
			});
		}
	}, [
		migrationId,
		mappingState,
		readCSVPreview,
		advancedOptions,
		projectData,
		setMappingState,
		updateAdvancedOptions,
		createAnalyticsEvent,
	]);

	const onKeyDownHandler = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (event.key === 'Tab') {
				setNextSelectedColumn(!event.shiftKey);
			}
		},
		[setNextSelectedColumn],
	);

	const onColumnResizeEnd = useCallback(
		({ column, width }: { column: ColumnShape<RowData>; width: number }) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			setColumnWidths({ ...columnWidths, [column.key as string]: width });
		},
		[columnWidths],
	);

	useEffect(() => {
		onReadCSV();
	}, [onReadCSV]);

	const shouldScrollToStart = hasTableColumnErrors(mappingState);
	useEffect(() => {
		// scroll table to the start when there are column validation errors
		if (shouldScrollToStart) {
			tableRef.current?.scrollToLeft(0);
		}
	}, [shouldScrollToStart, mappingState?.validationError]);

	useEffect(() => {
		if (mappingState && mappingState.selectedColumn) {
			const newScrollLeftPosition = getNewScrollLeftPosition(
				mappingState,
				columnWidths,
				mappingState.selectedColumn,
				tableRef?.current,
			);

			newScrollLeftPosition !== undefined && tableRef.current?.scrollToLeft(newScrollLeftPosition);
		}

		// Only scroll if selected column changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mappingState?.selectedColumn]);

	const getColumnNameRow = useCallback((): RowData[] => {
		if (!mappingState) return [];

		const columnNameRow: RowData = {};
		mappingState.columnOrder.forEach((columnName) => {
			columnNameRow[columnName] = columnName;
		});

		return [columnNameRow];
	}, [mappingState]);

	if (hasErrorOccurred) {
		return (
			<DisabledWrapper>
				<Image src={ReadErrorIcon} aria-describedby="mapping-table-read-error" role="img" alt="" />
				<ErrorDescription level="h700" as="h3" id="mapping-table-read-error">
					{formatMessage(messages.error)}
				</ErrorDescription>
				<Button appearance="primary" onClick={onReadCSV}>
					{formatMessage(messages.retry)}
				</Button>
			</DisabledWrapper>
		);
	}

	if (isLoading || mappingState === null || isPrioritiesLoading) {
		return (
			<DisabledWrapper data-testid="project-create-importer-upload-flow.ui.mapping-screen.mapping-table.disabled-wrapper">
				<Spinner size="xlarge" />
				<LoadingDescription level="h400" as="span">
					{formatMessage(messages.loading)}
				</LoadingDescription>
			</DisabledWrapper>
		);
	}

	const footerOptions =
		mappingState.rows.length >= 30
			? { footerHeight: 20, footerRenderer: <span>{formatMessage(messages.preview)}</span> }
			: {};

	const headerRenderer = ({ cells }: { cells: ReactNode[] }) => (
		<SpotlightTarget name={HEADER_X2C_NUDGE_STEP}>
			<ConditionalWrapper
				condition={nudgeStep === HEADER_X2C_NUDGE_STEP}
				renderWrapper={(children) => <HeaderRow>{children}</HeaderRow>}
			>
				{cells}
			</ConditionalWrapper>
		</SpotlightTarget>
	);

	return (
		<Wrapper data-testid="project-create-importer-upload-flow.ui.mapping-screen.mapping-table.wrapper">
			<TableWrapper
				onKeyDown={onKeyDownHandler}
				data-testid="project-create-importer-upload-flow.ui.mapping-screen.mapping-table.table-wrapper"
			>
				<AutoResizer>
					{({ width, height }) => (
						<BaseTable
							// @ts-expect-error - TS2769 - No overload matches this call.
							ref={tableRef}
							fixed
							width={width}
							height={height}
							columns={columns}
							data={mappingState.rows}
							frozenData={getColumnNameRow()}
							onColumnResizeEnd={onColumnResizeEnd}
							headerHeight={HEADER_HEIGHT}
							{...footerOptions}
							headerRenderer={headerRenderer}
						/>
					)}
				</AutoResizer>
			</TableWrapper>
		</Wrapper>
	);
};

MappingTable.displayName = 'MappingTable';

export default memo(MappingTable);

const defaultBorder = `1px solid ${token('color.border', colors.N40)}`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	flex: '1 1 auto',
	minHeight: '200px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DisabledWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	flex: '1 1 auto',
	borderRadius: '4px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.disabled', colors.N20),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		marginBottom: token('space.200', '16px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled.img({
	width: '96px',
	height: '96px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorDescription = styled(Heading)({
	marginTop: token('space.100', '8px'),
	marginBottom: token('space.500', '40px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingDescription = styled(Heading)({
	marginTop: token('space.500', '40px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N600),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableWrapper = styled.div({
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('elevation.surface', colors.N0),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text', colors.N800),
		borderRadius: '8px',
		border: defaultBorder,
		position: 'relative',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__table': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('elevation.surface', colors.N0),
		position: 'absolute',
		top: 0,
		display: 'flex',
		flexDirection: 'column-reverse',
		overflow: 'auto',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__footer': {
		position: 'absolute',
		bottom: 0,
		backgroundColor: token('color.background.neutral', '#FAFBFC'),
		color: token('color.text.subtlest', '#7A869A'),
		fontSize: '14px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__header-row': {
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		fontSize: '13px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__row': {
		display: 'flex',
		flexDirection: 'row',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__header-cell': {
		position: 'relative',
		minWidth: 0,
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		padding: token('space.025', '2px'),
		whiteSpace: 'nowrap',
		border: defaultBorder,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.neutral', colors.N20),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__row--frozen .BaseTable__row-cell': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.skeleton.subtle', colors.N10),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__row .BaseTable__row-cell': {
		minWidth: 0,
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		padding: token('space.100', '8px'),
		boxSizing: 'border-box',
		whiteSpace: 'nowrap',
		border: defaultBorder,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__header-cell:hover .BaseTable__column-resizer': {
		visibility: 'visible',
		opacity: 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__column-resizer': {
		width: '10px',
		visibility: 'hidden',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__column-resizer::before': {
		position: 'absolute',
		right: 0,
		top: 0,
		bottom: 0,
		overflow: 'hidden',
		content: "''",
		width: '2px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.border', colors.N30),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.BaseTable__header-row--resizing .BaseTable__header-cell': {
		cursor: 'col-resize',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderRow = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: 'calc(100vw - 160px)',
	height: '100%',
});
